import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { DataService } from 'src/app/services/data.service';
import RewardDTO from '../../dtos/RewardDTO';
import { MessageService } from 'primeng/api';
import { UtilitiesService } from 'src/app/common/services/utilities.service';
@Component({
  selector: 'prizes-card-form-group',
  templateUrl: './prizes-card-form-group.component.html',
  styleUrls: ['./prizes-card-form-group.component.sass']
})
export class PrizesCardFormGroupComponent implements OnInit, OnChanges {
  @Input() prize: Array<RewardDTO> = [];
  @Input() showPrize: boolean = true;
  @Input() fieldsetName: string = "";
  @Input() excludeResourceTypes: Array<string> = [];
  @Input() currenciesInclusion: number[] | null = null; // list of currency ids to include
  @Input() useOptionValue: boolean = false;
  @Input() isCostField: boolean = false;
  @Input() isChallengePrize: boolean = false;
  @Input() isChallenge: boolean = false;
  @Output() onPrizeChange = new EventEmitter<any>();
  @Output() DeletedIndex = new EventEmitter<any>();
  @Output() onChallengePrizeChange = new EventEmitter<any>();
  @Input() defaultResourceType: keyof typeof this.resourceMap | null = null;

  isLoading: boolean = true;
  options: any = {};
  suggestions: { [key: string]: any[] } = {};

  get resourceMap() {
    return {
      Currency: 'currencies',
      Item: 'items',
      Titles: 'titles',
      SeedPackBox: 'seed-pack-box',
      StoreListing: 'store-listings-v2',
      Nurture: 'nurture',
      DynamicMysteryBox: 'dynamic-mystery-box',
    };
  }

  constructor
    (
      private dataService: DataService,
      private messageService: MessageService,
      private utilitiesService: UtilitiesService
    ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['prize'] && !changes['prize'].firstChange) {
      this.normalizeRewards();
    }
  }

  /**
   * Normalizes rewards to ensure all required fields are set
   */
  private normalizeRewards() {
    this.prize?.forEach((reward: any) => {
      // Match reward.t to the actual option object reference so p-dropdown can display it correctly.
      // This is needed when rewards are set programmatically (e.g. when the challenge type changes)
      // because p-dropdown uses reference equality for matching the selected value to its options.
      if (reward && reward.t && this.options['t'] && this.options['t'].length > 0 && !this.options['t'].includes(reward.t)) {
        const tValue = reward.t;
        const matchedResource = this.options['t'].find((r: any) => {
          if (tValue._id) return String(r._id) === String(tValue._id);
          if (tValue.name) return r.name === tValue.name;
          return false;
        });
        if (matchedResource) {
          reward.t = matchedResource;
          reward.lineItemType = matchedResource.name;
        }
      }
      if (reward.id && typeof reward.id === 'object' && reward.id !== null) {
        if (!reward.id._id && !reward.id.name) {
          reward.id = null;
        } else if (!reward.id.displayName && reward.id.name) {
          reward.id.displayName = `${reward.id.name} (${reward.id.id})`;
        }
      }
      // Ensure lineItemType is set from t.name if it exists
      if (reward.t && reward.t.name && !reward.lineItemType) {
        reward.lineItemType = reward.t.name;
      }
      // Ensure count has a default value only if it's completely missing (not if it's a valid number)
      if (reward.c === undefined || reward.c === null) {
        reward.c = 0;
      }
    });
  }

  /**
   * Prize Card Form Group Component Initialization
   */
  async ngOnInit() {
    try {
      // Ensure prize is always an array
      if (!this.prize || !Array.isArray(this.prize)) {
        this.prize = [];
      }

      if (this.isCostField) {
        this.excludeResourceTypes = ['Titles', 'Item'];
      }

      await this.setOptions();

      // Normalize rewards after loading to ensure count values and displayNames are set
      this.normalizeRewards();
    } catch (error) {
      console.error('Error initializing prizes-card-form-group:', error);
    } finally {
      // Always set isLoading to false so the component renders
      this.isLoading = false;
    }
  }

  /**
   * Set options for dropdowns and auto-completes
   */
  async setOptions() {
    // Ensure prize is an array before iterating
    if (!this.prize || !Array.isArray(this.prize)) {
      this.prize = [];
    }
    
    // First load all the options
    await this.getOptionsFromRef('t', 'resources', true, true, this.excludeResourceTypes && this.excludeResourceTypes.length > 0 ? { name: { $nin: this.excludeResourceTypes } } : null);
    
    if(this.currenciesInclusion && this.currenciesInclusion.length > 0) {
      await this.getOptionsFromRef('id', 'currencies', true, true, { id: { $in: this.currenciesInclusion } });
    } else {
      await this.getOptionsFromRef('id', 'currencies', true, true);
    }
    
    await this.getOptionsFromRef('item-types', 'item-types', true, true);
    
    await this.getOptionsFromRef('user_titles', 'titles', true, true);
    
    // Now hydrate prize data with the loaded options
    for (const prize of this.prize) {
      try {
        // Handle prize.t (resource type) - need to assign the ACTUAL option object for p-dropdown to match
        if (prize && this.options['t'] && this.options['t'].length > 0) {
          let matchedResource = null;
          
          if (prize.t) {
            if (typeof prize.t === 'string') {
              // prize.t is a string - could be an ObjectId or a name like "Item"
              matchedResource = this.options['t'].find((r: any) => 
                r._id === prize.t || 
                r._id?.toString() === prize.t ||
                String(r._id) === String(prize.t) ||
                r.name === prize.t ||
                String(r.id) === String(prize.t)
              );
            } else if (prize.t._id) {
              // prize.t is an object with _id
              matchedResource = this.options['t'].find((r: any) => 
                r._id === prize.t._id || 
                r._id?.toString() === prize.t._id?.toString() ||
                String(r._id) === String(prize.t._id)
              );
            } else if (prize.t.name) {
              // prize.t is an object with name but no _id
              matchedResource = this.options['t'].find((r: any) => r.name === prize.t.name);
            }
          }
          
          // Also try matching by lineItemType if we haven't found a match
          if (!matchedResource && prize.lineItemType) {
            matchedResource = this.options['t'].find((r: any) => r.name === prize.lineItemType);
          }
          
          if (matchedResource) {
            // IMPORTANT: Assign the actual option object reference, not a copy!
            // p-dropdown compares by object reference
            prize.t = matchedResource;
            prize.lineItemType = matchedResource.name;
          }
        }
        
        // Handle prize.id - could be string ObjectId that needs to be looked up
        const prizeIdValue = prize?.id;
        const isIdString = typeof prizeIdValue === 'string';
        const isIdObjectWithId = prizeIdValue && typeof prizeIdValue === 'object' && (prizeIdValue._id || prizeIdValue.id);
        
        if (prize && isIdString) {
          // prize.id is a raw ObjectId string, we need to look it up
          const idValue = prizeIdValue;
          
          // Try to fetch the actual item data if we have lineItemType
          if (prize.lineItemType && this.resourceMap[prize.lineItemType as keyof typeof this.resourceMap]) {
            const model = this.resourceMap[prize.lineItemType as keyof typeof this.resourceMap];
            try {
              const items = await this.dataService.getAllOfTypeAsync(model, {
                query: { _id: idValue },
                select: '_id name id',
                virtuals: false,
                autopopulate: false
              });
              if (items && items.length > 0) {
                prize.id = {
                  _id: items[0]._id,
                  name: items[0].name,
                  id: items[0].id,
                  displayName: `${items[0].name} (${items[0].id})`
                };
              } else {
                // Item not found, set a placeholder
                prize.id = { _id: idValue, name: 'Unknown', displayName: `Unknown (${idValue.substring(0, 8)}...)` };
              }
            } catch (e) {
              console.warn('Could not fetch item for prize.id:', idValue, e);
              prize.id = { _id: idValue, name: 'Error', displayName: `Error loading item` };
            }
          } else {
            // No lineItemType, can't look up - set placeholder
            prize.id = { _id: idValue, name: '', displayName: idValue.substring(0, 12) + '...' };
          }
        } else if (prize && isIdObjectWithId) {
          // If the numeric id is missing, look it up from the loaded options
          if (!prizeIdValue.id && this.options['id']?.length > 0) {
            const matched = this.options['id'].find((o: any) =>
              String(o._id) === String(prizeIdValue._id) || o.name === prizeIdValue.name
            );
            if (matched) {
              prize.id.id = matched.id;
            }
          }
          // prize.id is an object - ensure it always has displayName
          prize.id.displayName = `${prizeIdValue.name || ''} (${prizeIdValue.id || ''})`.trim();
          if (prize.id.displayName === '()' || prize.id.displayName === '') {
            prize.id.displayName = prizeIdValue._id?.substring?.(0, 12) || 'Unknown';
          }
        }
        
        if (this.isCostField && prize && prize.id && typeof prize.id === 'object') {
          prize.id = prize.id._id;
        }
      } catch (e) {
        console.warn('Error processing prize item:', e);
      }
    }
  }

  /**
   * Get/set options from a reference entity type.
   *
   * @param fieldName Field name to populate
   * @param model API Controller route
   * @param minimal Flag that sets wether or not to set minimal values
   */
  async getOptionsFromRef
    (
      fieldName: string,
      model: string,
      minimal: boolean = false,
      autopopulate: boolean = true,
      customQuery: any = null
    ) {
    const options = await this.dataService.getAllOfTypeAsync(model,
      {
        query: customQuery ? customQuery : {},
        autopopulate: autopopulate,
        virtuals: true,
        sort: { name: 1 },
      });
    if (minimal) {
      let o: any[] = [];
      for (const option of options) {
        o.push({ name: option.name, id: option.id, _id: option._id });
      }
      this.options[fieldName] = o;
    }
    else {
      this.options[fieldName] = options;
    }
  }

  /**
   * Resolves string IDs to full objects for proper autocomplete display
   * Uses batched $in queries to minimize API calls
   */
  async resolveStringIdsToObjects() {
    if (this.isCostField) {
      // For cost fields, IDs are strings and handled by dropdown
      return;
    }

    // Ensure prize is an array before iterating
    if (!this.prize || !Array.isArray(this.prize)) {
      this.prize = [];
    }

    // Group rewards by resource type for batched queries
    const rewardsByType: Map<string, { ids: string[], rewards: any[] }> = new Map();

    for (const reward of this.prize) {
      // Only process if id is a string and we have a resource type
      if (reward.id && typeof reward.id === 'string' && reward.t && reward.t.name) {
        const resourceType = reward.t.name;
        const apiEndpoint = resourceType in this.resourceMap
          ? this.resourceMap[resourceType as keyof typeof this.resourceMap]
          : null;

        if (apiEndpoint) {
          if (!rewardsByType.has(apiEndpoint)) {
            rewardsByType.set(apiEndpoint, { ids: [], rewards: [] });
          }
          const group = rewardsByType.get(apiEndpoint)!;
          group.ids.push(reward.id);
          group.rewards.push(reward);
        }
      }
    }

    // Batch fetch for each resource type
    for (const [apiEndpoint, group] of rewardsByType) {
      if (group.ids.length === 0) continue;

      try {
        // Use $in query to fetch all IDs in one request
        const results = await this.dataService.getAllOfTypeAsync(apiEndpoint, {
          query: { _id: { $in: group.ids } },
          autopopulate: false,
          virtuals: false,
          select: '_id name id',
        });

        // Create a map for quick lookup
        const resultsMap = new Map(results.map((item: any) => [item._id, item]));

        // Update rewards with fetched data
        for (const reward of group.rewards) {
          const item = resultsMap.get(reward.id) as { _id: string; name: string; id: number } | undefined;
          if (item) {
            reward.id = {
              name: item.name,
              id: item.id,
              _id: item._id,
              displayName: `${item.name} (${item.id})`
            };
            // Ensure lineItemType is set from t.name
            if (reward.t && reward.t.name && !reward.lineItemType) {
              reward.lineItemType = reward.t.name;
            }
          }
        }
      } catch (error) {
        console.error(`Error batch resolving IDs for ${apiEndpoint}:`, error);
      }
    }
  }

  /**
   * Get suggestions for auto-complete
   *
   * @param event Event comming from the component
   * @param fieldName Name of the field to set suggestions
   * @param model API endpoint route
   */
  async getSuggestionsForRef
    (
      event: any,
      fieldName: string | any,
      model: string
    ) {
    // Validate that model is defined and event has minimum length
    if (!model || !event || event.length < 1) {
      this.suggestions[fieldName] = [];
      return;
    }

    // Check if the query is numeric (for searching by ID)
    const isNumeric = /^\d+$/.test(event);
    
    let queryObj: any;
    if (isNumeric) {
      // Search by ID or name containing the number
      queryObj = {
        $or: [
          { id: parseInt(event) },
          { name: { $regex: event, $options: 'i' } }
        ]
      };
    } else {
      // Search by name only
      queryObj = { name: { $regex: event, $options: 'i' } };
    }

    this.dataService.getAllOfType(model,
      {
        query: queryObj,
        select: '_id name id start end',
        virtuals: false,
        autopopulate: false,
        sort: { name: 1 },
        limit: 50 // Limit results to prevent performance issues
      })
      .subscribe({
        next: (result) => {
          // Using map to transform the result array more efficiently
          this.suggestions[fieldName] = result.map((item: { name: any; id: any; }) => ({
            ...item,
            displayName: `${item.name} (${item.id})` 
          }));
        },
        error: (error) => {
          console.error('Error fetching suggestions:', error);
          this.suggestions[fieldName] = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Search Error',
            detail: 'Failed to load suggestions. Please try again.'
          });
        }
      });
  }

  /**
   * Handles when an item is selected from the autocomplete
   * Ensures lineItemType is set from t.name
   */
  onItemSelected(index: number) {
    const reward = this.prize[index];
    // Only set lineItemType if it's missing - don't normalize all rewards
    if (reward.t && reward.t.name && !reward.lineItemType) {
      reward.lineItemType = reward.t.name;
    }
    this.onPrizeChange.emit(this.prize);
  }

  updateDependentField(event: any, index: number, isAutoComplete: boolean = false) {

    // Update the prize based on the selection
    if (event.value) {
      if (event.value.name !== this.prize[index].lineItemType) {
        this.prize[index].lineItemType = event.value.name;
        // Cleaning id since it now points to a different lineItemType
        this.prize[index].id = null;
      }
      if (this.prize[index].id && typeof this.prize[index].id === 'object' && this.prize[index].id !== null && !this.prize[index].id.displayName && this.prize[index].id.name) {
        this.prize[index].id.displayName = `${this.prize[index].id.name} (${this.prize[index].id.id})`;
      }
  
      if (event.value.name === 'Titles') {
        this.prize[index].c = 1;
      }

      if (event.value.name === 'SeedPackBox') {
        this.prize[index].c = 1;
      }

    } else {
      this.prize[index].lineItemType = '';
    }

    // Normalize rewards before emitting
    this.normalizeRewards();

    // Emit events based on whether the change is from the autocomplete componen
    // and if it's the first prize in the array
    if (isAutoComplete && index === 0 && this.isChallengePrize) {
      this.onChallengePrizeChange.emit(this.prize);
    } else {
      this.onPrizeChange.emit(this.prize);
    }
  }



  /**
   * Adds a new reward
   */
  addReward() {
    // Ensure prize is an array before reading/spreading it
    if (!this.prize || !Array.isArray(this.prize)) {
      this.prize = [];
    }

    if (this.isChallenge && this.prize.length >= 2) {
      // Using window.alert since we don't have access to a notification service
      this.messageService.add({severity: 'warn', summary: 'Warning', detail: 'Challenge prizes can only have 4.0 and 5.0 star prizes'});
      return;
    }

    let reward = {
      t: this.defaultResourceType !== null ? this.options['t'].find((option: any) => option.name === this.defaultResourceType) : {},
      id: {},
      c: 5,
      lineItemType: this.defaultResourceType !== null ? this.defaultResourceType : '',
    }
    this.prize = [...this.prize, reward];
    this.onPrizeChange.emit(this.prize);
  }


  /**
   * Deletes a reward from array of rewards
   *
   * @param index Index of the reward to remove.
   */
  deleteReward(index: number) {
    this.prize.splice(index, 1);
    this.onPrizeChange.emit(this.prize);
  }

  reorder(event: any) {
    this.onPrizeChange.emit(this.prize)
  }

  /**
   * Clears the autocomplete field for a specific reward at given index
   * @param index - The index of the reward to clear
   * @param fieldType - The field type to clear ('resource' or 'id')
   */
  clearAutoCompleteField(index: number, fieldType: 'resource' | 'id'): void {
    if (fieldType === 'resource') {
      this.prize[index].t = {};
      this.prize[index].lineItemType = '';
      // Also clear the id field since it depends on the resource type
      this.prize[index].id = {};
    } else if (fieldType === 'id') {
      this.prize[index].id = {};
    }
    
    // Emit the change
    this.onPrizeChange.emit(this.prize);
    
    // Show confirmation message
    this.messageService.add({
      severity: 'info',
      summary: 'Field Cleared',
      detail: `${fieldType === 'resource' ? 'Resource type' : 'Item selection'} has been cleared for Resource ${index + 1}`
    });
  }

  /**
   * Checks if a field has a value to show/hide the clear button
   * @param reward - The reward object to check
   * @param fieldType - The field type to check ('resource' or 'id')
   * @returns boolean indicating if the field has a value
   */
  hasFieldValue(reward: any, fieldType: 'resource' | 'id'): boolean {
    if (fieldType === 'resource') {
      return reward.t && (reward.t.name || reward.t._id);
    } else if (fieldType === 'id') {
      return reward.id && (reward.id.name || reward.id._id);
    }
    return false;
  }

  /**
   * Prevents space key event propagation in autocomplete fields
   * This fixes the issue where space key is intercepted by PrimeNG components
   * @param event - The keyboard event
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.code === 'Space') {
      event.stopPropagation();
    }
  }

  /**
   * Handles item selection from autocomplete to ensure proper display
   * @param event - The selection event from p-autoComplete
   * @param reward - The reward object being updated
   */
  onItemSelect(event: any, reward: any): void {
    if (reward && reward.id && typeof reward.id === 'object') {
      // Always ensure the selected item has displayName for proper display
      reward.id.displayName = `${reward.id.name || ''} (${reward.id.id || ''})`;
    }
  }

}
